/**
 * Navbar Component
 * Barra de navegación con logo, menú, selector de idioma y botón de login
 */

import { i18n, type Language } from '../utils/i18n';

export interface NavbarConfig {
  logoSrc: string;
  menuItems: Array<{
    labelKey: string;
    sectionId: string;
  }>;
  onLoginClick: () => void;
}

export class Navbar {
  private config: NavbarConfig;
  private element: HTMLElement | null = null;

  constructor(config: NavbarConfig) {
    this.config = config;
  }

  /**
   * Renderiza el navbar y retorna el elemento HTML
   */
  render(): HTMLElement {
    const nav = document.createElement('nav');
    nav.className = 'navbar';
    nav.setAttribute('role', 'navigation');
    nav.setAttribute('aria-label', 'Navegación principal');

    nav.innerHTML = `
      <div class="navbar-container">
        <!-- Logo (clickable - goes to hero) -->
        <a href="#hero" class="navbar-logo-link" aria-label="Ir al inicio">
          <div class="navbar-logo">
            <img src="${this.config.logoSrc}" alt="BeSafe Logo" class="navbar-logo-img" />
          </div>
        </a>

        <!-- Hamburger Button (Mobile only) -->
        <button class="navbar-hamburger" type="button" aria-label="Menú" aria-expanded="false">
          <span class="hamburger-line"></span>
          <span class="hamburger-line"></span>
          <span class="hamburger-line"></span>
        </button>

        <!-- Desktop Navigation -->
        <div class="navbar-desktop">
          <!-- Menu Container (Dark Background) -->
          <div class="navbar-menu-container">
            <ul class="navbar-menu" role="menubar">
              ${this.config.menuItems.map(item => `
                <li class="navbar-menu-item" role="none">
                  <a 
                    href="#${item.sectionId}" 
                    class="navbar-menu-link" 
                    data-section="${item.sectionId}"
                    data-i18n="${item.labelKey}"
                    role="menuitem"
                  >
                    ${i18n.t(item.labelKey)}
                  </a>
                </li>
              `).join('')}
            </ul>
          </div>

          <!-- Login Button -->
          <button class="navbar-action-btn navbar-login-btn" type="button" aria-label="Iniciar sesión" data-i18n="nav.login">
          ${i18n.t('nav.login')}
          </button>


          <!-- Language Selector -->
          <div class="navbar-language-selector">
            <button 
              class="language-btn ${i18n.getCurrentLanguage() === 'es' ? 'active' : ''}" 
              data-lang="es"
              type="button"
              aria-label="Español"
            >
              ES
            </button>
            <span class="language-separator">|</span>
            <button 
              class="language-btn ${i18n.getCurrentLanguage() === 'en' ? 'active' : ''}" 
              data-lang="en"
              type="button"
              aria-label="English"
            >
              EN
            </button>
          </div>
        </div>

        <!-- Mobile Menu (Hidden by default) -->
        <div class="navbar-mobile-menu">
          <ul class="mobile-menu-list">
            ${this.config.menuItems.map(item => `
              <li class="mobile-menu-item">
                <a 
                  href="#${item.sectionId}" 
                  class="mobile-menu-link" 
                  data-section="${item.sectionId}"
                  data-i18n="${item.labelKey}"
                >
                  ${i18n.t(item.labelKey)}
                </a>
              </li>
            `).join('')}
          </ul>

          <div class="mobile-menu-actions">
            <button class="mobile-action-btn mobile-payment-btn" type="button" data-i18n="hero.paymentCenter">
              ${i18n.t('hero.paymentCenter')}
            </button>
            <button class="mobile-action-btn mobile-status-btn" type="button" data-i18n="hero.checkStatus">
              ${i18n.t('hero.checkStatus')}
            </button>
            <button class="mobile-login-btn" type="button" data-i18n="nav.login">
              ${i18n.t('nav.login')}
            </button>
          </div>

          <div class="mobile-language-selector">
            <button 
              class="mobile-language-btn ${i18n.getCurrentLanguage() === 'es' ? 'active' : ''}" 
              data-lang="es"
              type="button"
            >
              ES
            </button>
            <span class="language-separator">|</span>
            <button 
              class="mobile-language-btn ${i18n.getCurrentLanguage() === 'en' ? 'active' : ''}" 
              data-lang="en"
              type="button"
            >
              EN
            </button>
          </div>
        </div>
      </div>
    `;

    this.element = nav;
    this.attachEventListeners();
    
    return nav;
  }

  /**
   * Adjunta event listeners a los elementos del navbar
   */
  private attachEventListeners(): void {
    if (!this.element) return;

    // Check if we're on the home page
    const isHomePage = window.location.pathname === '/' || window.location.pathname === '/index.html';

    // Hamburger menu toggle
    const hamburger = this.element.querySelector('.navbar-hamburger');
    const mobileMenu = this.element.querySelector('.navbar-mobile-menu');
    
    if (hamburger && mobileMenu) {
      hamburger.addEventListener('click', () => {
        const isExpanded = hamburger.getAttribute('aria-expanded') === 'true';
        hamburger.setAttribute('aria-expanded', (!isExpanded).toString());
        hamburger.classList.toggle('active');
        mobileMenu.classList.toggle('active');
        document.body.style.overflow = isExpanded ? 'auto' : 'hidden';
      });
    }

    // Close mobile menu when clicking outside
    document.addEventListener('click', (e) => {
      if (mobileMenu && hamburger) {
        const target = e.target as HTMLElement;
        if (!mobileMenu.contains(target) && !hamburger.contains(target) && mobileMenu.classList.contains('active')) {
          hamburger.classList.remove('active');
          mobileMenu.classList.remove('active');
          hamburger.setAttribute('aria-expanded', 'false');
          document.body.style.overflow = 'auto';
        }
      }
    });

    // Logo click - scroll to hero or go to home
    const logoLink = this.element.querySelector('.navbar-logo-link');
    if (logoLink) {
      logoLink.addEventListener('click', (e) => {
        e.preventDefault();
        if (isHomePage) {
          this.scrollToSection('hero');
        } else {
          window.location.href = '/';
        }
        this.closeMobileMenu();
      });
    }

    // Desktop Login button click
    const loginBtn = this.element.querySelector('.navbar-login-btn');
    if (loginBtn) {
      loginBtn.addEventListener('click', () => {
        this.config.onLoginClick();
      });
    }





    const mobileLoginBtn = this.element.querySelector('.mobile-login-btn');
    if (mobileLoginBtn) {
      mobileLoginBtn.addEventListener('click', () => {
        this.config.onLoginClick();
        this.closeMobileMenu();
      });
    }

    // Desktop menu items click
    const menuLinks = this.element.querySelectorAll('.navbar-menu-link');
    menuLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const sectionId = (link as HTMLElement).getAttribute('data-section');
        if (sectionId) {
          if (isHomePage) {
            this.scrollToSection(sectionId);
          } else {
            window.location.href = `/#${sectionId}`;
          }
        }
      });
    });

    // Mobile menu items click
    const mobileMenuLinks = this.element.querySelectorAll('.mobile-menu-link');
    mobileMenuLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const sectionId = (link as HTMLElement).getAttribute('data-section');
        if (sectionId) {
          if (isHomePage) {
            this.scrollToSection(sectionId);
          } else {
            window.location.href = `/#${sectionId}`;
          }
        }
        this.closeMobileMenu();
      });
    });

    // Desktop language selector buttons
    const languageBtns = this.element.querySelectorAll('.language-btn');
    languageBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const lang = (btn as HTMLElement).getAttribute('data-lang') as Language;
        if (lang) {
          this.handleLanguageChange(lang);
        }
      });
    });

    // Mobile language selector buttons
    const mobileLanguageBtns = this.element.querySelectorAll('.mobile-language-btn');
    mobileLanguageBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const lang = (btn as HTMLElement).getAttribute('data-lang') as Language;
        if (lang) {
          this.handleLanguageChange(lang);
        }
      });
    });
  }

  /**
   * Cierra el menú móvil
   */
  private closeMobileMenu(): void {
    if (!this.element) return;
    
    const hamburger = this.element.querySelector('.navbar-hamburger');
    const mobileMenu = this.element.querySelector('.navbar-mobile-menu');
    
    if (hamburger && mobileMenu) {
      hamburger.classList.remove('active');
      mobileMenu.classList.remove('active');
      hamburger.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = 'auto';
    }
  }

  /**
   * Maneja el cambio de idioma
   */
  private handleLanguageChange(lang: Language): void {
    // Update i18n system
    i18n.setLanguage(lang);

    // Update active state of language buttons
    if (this.element) {
      const languageBtns = this.element.querySelectorAll('.language-btn');
      languageBtns.forEach(btn => {
        const btnLang = (btn as HTMLElement).getAttribute('data-lang');
        if (btnLang === lang) {
          btn.classList.add('active');
        } else {
          btn.classList.remove('active');
        }
      });
    }
  }

  /**
   * Scroll suave a una sección
   */
  private scrollToSection(sectionId: string): void {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      
      // Update active menu item
      this.updateActiveMenuItem(sectionId);
    }
  }

  /**
   * Actualiza el item activo del menú
   */
  private updateActiveMenuItem(sectionId: string): void {
    if (!this.element) return;
    
    const menuLinks = this.element.querySelectorAll('.navbar-menu-link');
    menuLinks.forEach(link => {
      const linkSection = (link as HTMLElement).getAttribute('data-section');
      if (linkSection === sectionId) {
        link.classList.add('active');
      } else {
        link.classList.remove('active');
      }
    });
  }

  /**
   * Obtiene el elemento del navbar
   */
  getElement(): HTMLElement | null {
    return this.element;
  }
}
